/* eslint-disable react/no-array-index-key */
import { ReactElement } from 'react';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { RestartIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import {
  SubmitFeedbackSummaryPayload,
  WorksheetFeedbackSummary,
  WorksheetFeedbackSummaryEntry,
  useRateFeedbackSummary,
} from 'hooks/api/backend/worksheets/useWorksheets';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { WorksheetFeedbackSummaryReview } from './WorksheetFeedbackSummaryReview';

type SummaryListEntryProps = {
  emoji: string;
  entry: WorksheetFeedbackSummaryEntry;
};

const SummaryListEntry = ({ emoji, entry }: SummaryListEntryProps): ReactElement => (
  <HStack align="flex-start" gap="s" width="100%">
    <Box flexShrink={0} pt="2px" lineHeight="1" aria-hidden>
      <Text textStyle="body.m.regular">{emoji}</Text>
    </Box>
    <VStack align="stretch" gap="2xs" flex="1" minW={0}>
      <Text textStyle="body.m.medium" color="text.primary">
        {entry.heading}
      </Text>
      {entry.description ? (
        <Text textStyle="body.s.regular" color="text.tertiary">
          {entry.description}
        </Text>
      ) : null}
    </VStack>
  </HStack>
);

type Props = {
  summary: WorksheetFeedbackSummary;
  summaryClassId?: string;
  onRefresh: () => void;
  isRefreshing?: boolean;
  onSummarySubmitted?: () => void;
};

export const WorksheetFeedbackSummaryContent = ({
  summary,
  summaryClassId,
  onRefresh,
  isRefreshing = false,
  onSummarySubmitted,
}: Props): JSX.Element => {
  const { show: showToast } = useNovaToast();
  const rateSummary = useRateFeedbackSummary();

  const newResponsesCount = summary.currentSubmissionCount - summary.submissionsAtGeneration;
  const isSummaryStale = summary.currentSubmissionCount > summary.submissionsAtGeneration;

  const handleRefresh = (): void => {
    if (isRefreshing) return;
    onRefresh();
  };

  const handleSubmitFeedback = async (payload: SubmitFeedbackSummaryPayload): Promise<void> => {
    if (isRefreshing) return;

    try {
      await rateSummary.mutateAsync({
        summaryId: payload.summaryId,
        worksheetId: summary.worksheetId,
        classId: summaryClassId,
        rating: payload.rating,
        message: payload.message,
        shortMessages: payload.shortMessages ?? [],
      });
      onSummarySubmitted?.();
    } catch {
      showToast({
        description: 'Das Feedback konnte nicht gespeichert werden.',
        variant: ToastVariant.ERROR,
      });
      throw new Error('Rate feedback failed');
    }
  };

  return (
    <VStack align="stretch" gap="m" width="100%">
      {isSummaryStale && newResponsesCount > 0 && (
        <HStack
          align="center"
          gap="s"
          width="100%"
          bg="#fff7dc"
          border="1px solid"
          borderColor="#ffedb2"
          borderRadius="m"
          p="s"
        >
          <NovaIcon icon={RestartIcon} size="s" color="icon.primary" aria-hidden />
          <Text textStyle="body.s.regular" color="text.primary">
            {newResponsesCount === 1
              ? 'Eine weitere Person hat geantwortet. '
              : `${newResponsesCount} weitere Personen haben geantwortet. `}
            <Button
              variant="tertiary"
              size="m"
              onClick={handleRefresh}
              disabled={isRefreshing}
              loading={isRefreshing}
              textDecoration="underline"
              height="auto"
              minH="0"
              p={0}
              verticalAlign="baseline"
            >
              Zusammenfassung aktualisieren
            </Button>
          </Text>
        </HStack>
      )}

      <VStack align="stretch" gap="xs">
        <Text textStyle="body.m.medium" color="text.primary">
          Gesamtbewertung
        </Text>
        <Text textStyle="body.s.regular" color="text.primary">
          {summary.overallAssessment}
        </Text>
      </VStack>

      <VStack align="stretch" gap="m">
        <Text textStyle="body.m.medium" color="text.primary">
          Hauptprobleme
        </Text>
        {summary.mainIssues.map((issue, index) => (
          <SummaryListEntry key={`${issue.heading}-${index}`} emoji="❌" entry={issue} />
        ))}
      </VStack>

      <VStack align="stretch" gap="m">
        <Text textStyle="body.m.medium" color="text.primary">
          Mögliche Ursachen
        </Text>
        {summary.potentialReasons.map((reason, index) => (
          <SummaryListEntry key={`${reason.heading}-${index}`} emoji="💫" entry={reason} />
        ))}
      </VStack>

      <Text textStyle="body.xs.regular" color="text.tertiary">
        Basiert auf {summary.participantCount} Antworten • Erstellt am{' '}
        {new Date(summary.generatedAt).toLocaleDateString('de-DE')} von{' '}
        {summary.generatedBy.firstName} {summary.generatedBy.lastName}
      </Text>

      <WorksheetFeedbackSummaryReview
        key={summary.id}
        summaryId={summary.id}
        onSubmit={handleSubmitFeedback}
        isSubmitting={rateSummary.isPending}
        disabled={isRefreshing}
      />
    </VStack>
  );
};
